import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Select,
  Checkbox,
  ChoiceList,
  Button,
  Banner,
  Divider,
  Box,
} from '@shopify/polaris';
import { estimatesApi, productsApi } from '@/services';
import type { CreateEstimateInput, Address } from '@/services/estimates';
import type { RentalProductConfig } from '@/services/products';

type EstimateType = 'sale' | 'rental';

const PROVINCE_OPTIONS = [
  { label: 'Ontario', value: 'ON' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Alberta', value: 'AB' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland and Labrador', value: 'NL' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Yukon', value: 'YT' },
  { label: 'Nunavut', value: 'NU' },
];

interface SaleLineState {
  productId: string;
  variantId: string;
  productName: string;
  productSku: string;
  description: string;
  quantity: number;
  rateCents: number;
}

const RENTAL_BLURB = `3 in 1 multi-modal technology:
1. Compression garment (75 mmHg) (edema drainage, lymphatic system instigation and drainage
2. Bracing garment (semi-rigid with rigid plastic support, pneumatic)
3. Cold Therapy/Cryotherapy with cuff (self contained, electronic, sealed dry system)

12 clinically proven benefits claims for the Game Ready System:
1. Reduces patient narcotic consumption
2. Allows earlier discontinuation of narcotic pain medication
3. Improves key, measurable physical therapy milestones
4. Increases patient satisfaction with the recovery process
5. Reduces blood transfusion rates
6. Reduces postoperative blood loss
7. Shortens hospital admission time
8. Provides less wound discharge
9. Reduces risk of infection
10. Decreases pain
11. Decreases swelling
12. Increases postoperative function`;

export default function EstimateCreate() {
  const navigate = useNavigate();
  const [type, setType] = useState<EstimateType | null>(null);

  const productsQuery = useQuery({
    queryKey: ['products.list'],
    queryFn: () => productsApi.list(),
    enabled: !!type,
  });

  // Common patient + address state
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [shipStreet, setShipStreet] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipProvince, setShipProvince] = useState('ON');
  const [shipPostalCode, setShipPostalCode] = useState('');
  const [shipVia, setShipVia] = useState('');
  const [estimateDate, setEstimateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');

  // Sale-specific state
  const [saleLines, setSaleLines] = useState<SaleLineState[]>([]);

  // Rental-specific state
  const [rentalProductId, setRentalProductId] = useState('');
  const [rentalVariantId, setRentalVariantId] = useState('');
  const [rentalWeeks, setRentalWeeks] = useState(2);
  const [rentalBilateral, setRentalBilateral] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const products: RentalProductConfig[] = productsQuery.data ?? [];
  const rentalProducts = products.filter((p) => p.rentalConfig?.isRentalProduct);
  const selectedRentalProduct = rentalProducts.find(
    (p) => p.shopifyProductId === rentalProductId,
  );

  const rentalRateCents = selectedRentalProduct?.rentalConfig?.weeklyRateCents ?? 0;
  const rentalAmountCents = rentalRateCents * rentalWeeks;

  // Sale subtotal
  const saleSubtotalCents = saleLines.reduce((sum, l) => sum + l.quantity * l.rateCents, 0);

  function addSaleLine() {
    setSaleLines((prev) => [...prev, {
      productId: '', variantId: '', productName: '', productSku: '', description: '', quantity: 1, rateCents: 0,
    }]);
  }

  function updateSaleLine(idx: number, patch: Partial<SaleLineState>) {
    setSaleLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }

  function removeSaleLine(idx: number) {
    setSaleLines((prev) => prev.filter((_, i) => i !== idx));
  }

  function pickSaleProduct(idx: number, variantId: string) {
    if (!variantId) return;
    for (const p of products) {
      const v = p.variants.find((vv) => vv.id === variantId);
      if (v) {
        const priceCents = Math.round(parseFloat(v.price) * 100);
        updateSaleLine(idx, {
          productId: p.shopifyProductId,
          variantId: v.id,
          productName: p.title,
          productSku: v.sku ?? p.title,
          rateCents: priceCents,
        });
        return;
      }
    }
  }

  const variantOptions = useMemo(() => {
    const opts: Array<{ label: string; value: string }> = [{ label: 'Select a product…', value: '' }];
    for (const p of products) {
      for (const v of p.variants) {
        const variantTitle = v.title && v.title !== 'Default Title' ? ` — ${v.title}` : '';
        opts.push({ label: `${p.title}${variantTitle} ($${v.price})`, value: v.id });
      }
    }
    return opts;
  }, [products]);

  const rentalProductOptions = useMemo(() => [
    { label: 'Select a rental product…', value: '' },
    ...rentalProducts.map((p) => ({ label: p.title, value: p.shopifyProductId })),
  ], [rentalProducts]);

  const rentalVariantOptions = useMemo(() => {
    if (!selectedRentalProduct) return [{ label: 'Pick a product first', value: '' }];
    const variants = selectedRentalProduct.variants;
    if (variants.length <= 1) {
      return variants.length === 1
        ? [{ label: variants[0].title, value: variants[0].id }]
        : [{ label: 'No variants', value: '' }];
    }
    return [
      { label: 'Select a variant…', value: '' },
      ...variants.map((v) => ({ label: v.title, value: v.id })),
    ];
  }, [selectedRentalProduct]);

  function buildPayload(): CreateEstimateInput | null {
    if (!type) return null;
    const shipAddress: Address = {
      street: shipStreet, city: shipCity, province: shipProvince, postalCode: shipPostalCode,
    };
    const common = {
      type,
      patientFirstName,
      patientLastName,
      patientEmail: patientEmail || undefined,
      patientPhone: patientPhone || undefined,
      shipAddress,
      shipVia: shipVia || undefined,
      estimateDate,
      expirationDate,
      notes: notes || undefined,
    };

    if (type === 'sale') {
      return {
        ...common,
        lines: saleLines.map((l) => ({
          activity: 'GR Sales',
          shopifyProductId: l.productId || undefined,
          shopifyVariantId: l.variantId || undefined,
          productSku: l.productSku || l.productName,
          productName: l.productName,
          description: l.description,
          quantity: l.quantity,
          rateCents: l.rateCents,
        })),
      };
    } else {
      const variant = selectedRentalProduct?.variants.find((v) => v.id === rentalVariantId);
      return {
        ...common,
        lines: [{
          activity: 'GR Rental',
          shopifyProductId: rentalProductId || undefined,
          shopifyVariantId: rentalVariantId || undefined,
          productSku: variant?.sku ?? selectedRentalProduct?.title ?? '',
          productName: selectedRentalProduct?.title ?? '',
          description: RENTAL_BLURB,
          quantity: rentalWeeks,
          rateCents: rentalRateCents,
          isBilateral: rentalBilateral,
        }],
      };
    }
  }

  const createMutation = useMutation({
    mutationFn: async (markSent: boolean) => {
      const payload = buildPayload();
      if (!payload) throw new Error('No payload');
      const created = await estimatesApi.create(payload);
      if (markSent) {
        await estimatesApi.markSent(created.id);
      }
      return created;
    },
    onSuccess: (created) => {
      navigate(`/estimates/${created.id}`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create estimate';
      setSubmitError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    },
  });

  const formValid = (() => {
    if (!type) return false;
    if (!patientFirstName.trim() || !patientLastName.trim()) return false;
    if (!shipStreet.trim() || !shipCity.trim() || !shipPostalCode.trim()) return false;
    if (!estimateDate || !expirationDate) return false;
    if (type === 'sale') {
      if (saleLines.length === 0) return false;
      return saleLines.every((l) => l.productName && l.quantity > 0 && l.rateCents > 0);
    }
    return !!rentalProductId && !!rentalVariantId && rentalWeeks > 0 && rentalRateCents > 0;
  })();

  if (!type) {
    return (
      <Page title="New Estimate" backAction={{ content: 'Estimates', onAction: () => navigate('/estimates') }}>
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">What kind of estimate?</Text>
            <ChoiceList
              title=""
              choices={[
                { label: 'Sale Estimate (one or more products at retail price)', value: 'sale' },
                { label: 'Rental Estimate (Game Ready equipment, weekly rate)', value: 'rental' },
              ]}
              selected={[]}
              onChange={(values) => setType(values[0] as EstimateType)}
            />
          </BlockStack>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={`New ${type === 'sale' ? 'Sale' : 'Rental'} Estimate`}
      backAction={{ content: 'Estimates', onAction: () => navigate('/estimates') }}
    >
      <BlockStack gap="400">
        {submitError && <Banner tone="critical">{submitError}</Banner>}

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Patient</Text>
            <InlineStack gap="200" blockAlign="end">
              <TextField label="First name" value={patientFirstName} onChange={setPatientFirstName} autoComplete="given-name" requiredIndicator />
              <TextField label="Last name" value={patientLastName} onChange={setPatientLastName} autoComplete="family-name" requiredIndicator />
            </InlineStack>
            <InlineStack gap="200" blockAlign="end">
              <TextField label="Email (optional)" value={patientEmail} onChange={setPatientEmail} type="email" autoComplete="email" />
              <TextField label="Phone (optional)" value={patientPhone} onChange={setPatientPhone} type="tel" autoComplete="tel" />
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Shipping address</Text>
            <TextField label="Street" value={shipStreet} onChange={setShipStreet} autoComplete="street-address" requiredIndicator />
            <InlineStack gap="200" blockAlign="end">
              <TextField label="City" value={shipCity} onChange={setShipCity} autoComplete="address-level2" requiredIndicator />
              <Select label="Province" options={PROVINCE_OPTIONS} value={shipProvince} onChange={setShipProvince} />
              <TextField label="Postal code" value={shipPostalCode} onChange={setShipPostalCode} autoComplete="postal-code" requiredIndicator />
            </InlineStack>
            <TextField label="Ship via (optional)" value={shipVia} onChange={setShipVia} autoComplete="off" placeholder="UPS" />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Dates</Text>
            <InlineStack gap="200" blockAlign="end">
              <TextField label="Estimate date" type="date" value={estimateDate} onChange={setEstimateDate} autoComplete="off" />
              <TextField label="Expiration date" type="date" value={expirationDate} onChange={setExpirationDate} autoComplete="off" />
            </InlineStack>
          </BlockStack>
        </Card>

        {type === 'sale' ? (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingSm">Line items</Text>
                <Button onClick={addSaleLine}>+ Add line</Button>
              </InlineStack>
              {saleLines.length === 0 ? (
                <Text as="p" tone="subdued">No lines yet — add one to get started.</Text>
              ) : saleLines.map((line, idx) => (
                <Box key={idx} padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="200">
                    <Select
                      label="Product"
                      options={variantOptions}
                      value={line.variantId}
                      onChange={(v) => pickSaleProduct(idx, v)}
                    />
                    <TextField
                      label="Description (optional)"
                      value={line.description}
                      onChange={(v) => updateSaleLine(idx, { description: v })}
                      multiline={2}
                      autoComplete="off"
                    />
                    <InlineStack gap="200" blockAlign="end">
                      <TextField
                        label="Qty"
                        type="number"
                        value={String(line.quantity)}
                        onChange={(v) => updateSaleLine(idx, { quantity: parseInt(v, 10) || 0 })}
                        autoComplete="off"
                      />
                      <TextField
                        label="Rate (cents)"
                        type="number"
                        value={String(line.rateCents)}
                        onChange={(v) => updateSaleLine(idx, { rateCents: parseInt(v, 10) || 0 })}
                        autoComplete="off"
                        helpText={`= $${(line.rateCents / 100).toFixed(2)}`}
                      />
                      <Text as="p">{`Amount: $${((line.quantity * line.rateCents) / 100).toFixed(2)}`}</Text>
                      <Button tone="critical" variant="plain" onClick={() => removeSaleLine(idx)}>Remove</Button>
                    </InlineStack>
                  </BlockStack>
                </Box>
              ))}
              <Divider />
              <Text as="p" variant="headingMd">{`Subtotal: $${(saleSubtotalCents / 100).toFixed(2)}`}</Text>
            </BlockStack>
          </Card>
        ) : (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Rental equipment</Text>
              <Select
                label="Product"
                options={rentalProductOptions}
                value={rentalProductId}
                onChange={(v) => { setRentalProductId(v); setRentalVariantId(''); }}
              />
              {selectedRentalProduct && (
                <Select
                  label="Variant"
                  options={rentalVariantOptions}
                  value={rentalVariantId}
                  onChange={setRentalVariantId}
                />
              )}
              <InlineStack gap="200" blockAlign="end">
                <TextField
                  label="Weeks"
                  type="number"
                  value={String(rentalWeeks)}
                  onChange={(v) => setRentalWeeks(Math.max(1, parseInt(v, 10) || 1))}
                  autoComplete="off"
                />
                <Text as="p">{`Rate: $${(rentalRateCents / 100).toFixed(2)} / wk`}</Text>
                <Text as="p" variant="headingMd">{`Subtotal: $${(rentalAmountCents / 100).toFixed(2)}`}</Text>
              </InlineStack>
              {selectedRentalProduct?.rentalConfig?.bilateralPriceCents != null && (
                <Checkbox
                  label={`Bilateral (one-time +$${(selectedRentalProduct.rentalConfig.bilateralPriceCents / 100).toFixed(2)})`}
                  checked={rentalBilateral}
                  onChange={setRentalBilateral}
                />
              )}
            </BlockStack>
          </Card>
        )}

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Notes</Text>
            <TextField
              label="Internal notes (optional)"
              value={notes}
              onChange={setNotes}
              multiline={3}
              autoComplete="off"
            />
          </BlockStack>
        </Card>

        <InlineStack gap="200" align="end">
          <Button
            variant="primary"
            loading={createMutation.isPending}
            disabled={!formValid || createMutation.isPending}
            onClick={() => createMutation.mutate(false)}
          >
            Save as Draft
          </Button>
          <Button
            variant="primary"
            tone="success"
            loading={createMutation.isPending}
            disabled={!formValid || createMutation.isPending}
            onClick={() => createMutation.mutate(true)}
          >
            Save &amp; Mark Sent
          </Button>
        </InlineStack>
      </BlockStack>
    </Page>
  );
}
